import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  addDays,
  daysBetween,
  netPerExtraHour,
  planFortnights,
  planWeeks,
} from '@actual-app/core/shared/mtxbudget';
import type { Coverage, Fortnight } from '@actual-app/core/shared/mtxbudget';

import { useNavigate } from '#hooks/useNavigate';

import {
  BanksBanner,
  useWeekLabel,
  useWhenPaid,
  weekRange,
  WeekRow,
  workDays,
} from './semaines';
import {
  hoursLabel,
  Line,
  longDate,
  MtxPage,
  Section,
  shortDate,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';

const UPCOMING_DAYS = 15;

export function NotConfigured() {
  const { t } = useTranslation();
  return (
    <Section style={{ padding: 16 }}>
      <Text>
        {t(
          "MTXbudget n'est pas encore configuré : la structure de la paie, les dettes et les comptes courants doivent être renseignés.",
        )}
      </Text>
    </Section>
  );
}

function parseHours(value: string): number | null {
  const hours = Number(value.replace(',', '.'));
  return Number.isFinite(hours) && hours >= 0 && hours <= 168 ? hours : null;
}

export function QuinzainePage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const {
    config,
    saveConfig,
    plan,
    open,
    today,
    isLoading,
    occurrences,
    available,
    toComplete,
  } = useMtxbudget();
  const [target, setTarget] = useState('');
  const weekLabel = useWeekLabel();
  const whenPaid = useWhenPaid();

  function coverageLabel(coverage: Coverage): { text: string; color: string } {
    switch (coverage.kind) {
      case 'covered':
        return {
          text: t('Couvert par la paie du {{date}}', {
            date: textDate(coverage.payday),
          }),
          color: theme.noticeTextLight,
        };
      case 'set-aside':
        return {
          text: t('Mis de côté sur la paie du {{date}}', {
            date: textDate(coverage.payday),
          }),
          color: theme.pageTextSubdued,
        };
      default:
        return {
          text: t('Non couvert : il manque {{amount}}', {
            amount: money(coverage.missing),
          }),
          color: theme.errorText,
        };
    }
  }

  if (!config) {
    return (
      <MtxPage title={t('Ma quinzaine')}>
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  const pay = config.pay;
  const minHours: [number, number] = [pay.minHoursPerWeek, pay.minHoursPerWeek];
  const hoursOf = (fortnight: Fortnight) =>
    config.pays[fortnight.payday]?.hours ?? minHours;

  function setHours(fortnight: Fortnight, week: 0 | 1, value: string) {
    const hours = parseHours(value);
    if (hours === null || !config) {
      return;
    }
    const next: [number, number] = [...hoursOf(fortnight)];
    next[week] = hours;
    saveConfig({
      ...config,
      pays: {
        ...config.pays,
        [fortnight.payday]: { ...config.pays[fortnight.payday], hours: next },
      },
    });
  }

  const horizon = addDays(today, UPCOMING_DAYS);
  const upcoming = plan
    .flatMap(f => f.charges)
    .filter(charge => charge.date <= horizon);
  const upcomingTotal = upcoming.reduce((sum, c) => sum - c.amount, 0);
  const planned = open ? hoursOf(open) : minHours;
  // Which week of the open pay period we are in, and what a target of hours
  // for that week would leave.
  const week: 0 | 1 = open && daysBetween(open.periodStart, today) >= 7 ? 1 : 0;
  const targetHours = parseHours(target);
  const targeted: [number, number] | null =
    open && target !== '' && targetHours !== null
      ? week === 0
        ? [targetHours, planned[1]]
        : [planned[0], targetHours]
      : null;
  const targetMargin =
    open && targeted
      ? (planFortnights({
          config: pay,
          today,
          occurrences,
          pays: {
            ...config.pays,
            [open.payday]: { ...config.pays[open.payday], hours: targeted },
          },
          available,
          count: plan.length,
        }).find(f => f.payday === open.payday)?.margin ?? null)
      : null;

  function keepTarget() {
    if (!config || !open || !targeted) {
      return;
    }
    saveConfig({
      ...config,
      pays: {
        ...config.pays,
        [open.payday]: { ...config.pays[open.payday], hours: targeted },
      },
    });
    setTarget('');
  }

  // Every figure is told from today: this week, the next ones, the weeks done.
  const weeks = planWeeks(plan, today, pay.minHoursPerWeek);
  const current = weeks.find(w => w.offset === 0);
  const fortnight = current && plan.find(f => f.payday === current.payday);
  const following = weeks.filter(w => w.offset >= 1 && w.offset <= 3);
  const worked = plan.find(f => f.status === 'worked');
  const workedWeeks = worked
    ? weeks.filter(w => w.payday === worked.payday)
    : [];
  const fortnightHours = fortnight ? hoursOf(fortnight) : minHours;
  const fortnightPlanned = (fortnightHours[0] + fortnightHours[1]) / 2;
  const isSunday = new Date(`${today}T00:00:00Z`).getUTCDay() === 0;

  return (
    <MtxPage title={t('Ma quinzaine')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Text
          style={{
            fontSize: 13,
            color: theme.pageTextSubdued,
            padding: '0 4px',
          }}
        >
          {t("Aujourd'hui, {{date}}", { date: longDate(today) })}
        </Text>

        {toComplete.length > 0 && (
          <Button
            variant="bare"
            onPress={() => navigate('/quinzaine/a-completer')}
            style={{
              justifyContent: 'flex-start',
              gap: 12,
              minHeight: 64,
              padding: '12px 14px 12px 16px',
              borderRadius: 4,
              border: `1px solid ${theme.buttonPrimaryBackground}`,
              backgroundColor: theme.tableBackground,
              textAlign: 'left',
            }}
          >
            <View
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 16,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.buttonPrimaryBackground,
              }}
            >
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: theme.buttonPrimaryText,
                }}
              >
                {toComplete.length}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
                {toComplete.length === 1
                  ? t('1 montant à compléter')
                  : t('{{count}} montants à compléter', {
                      count: toComplete.length,
                    })}
              </Text>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                {t(
                  'Les banques ne les transmettent pas. Ils changent vos heures.',
                )}
              </Text>
            </View>
            <Text
              aria-hidden="true"
              style={{ fontSize: 18, color: theme.pageTextSubdued }}
            >
              ›
            </Text>
          </Button>
        )}

        {current && fortnight && (
          <Section style={{ padding: 16, gap: 10 }}>
            <Text
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: theme.pageTextDark,
              }}
            >
              {weekLabel(0, today)}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 8,
              }}
            >
              <Text
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: 52,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: theme.pageTextDark,
                  ...styles.tnum,
                }}
              >
                {hoursLabel(current.hours)}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color:
                    current.aboveBase > 0
                      ? theme.warningText
                      : theme.noticeTextLight,
                }}
              >
                {current.aboveBase > 0
                  ? t('{{hours}} de plus que votre base', {
                      hours: hoursLabel(current.aboveBase),
                    })
                  : t('votre base suffit')}
              </Text>
            </View>
            <Text style={{ fontSize: 14, color: theme.pageText }}>
              {current.aboveBase > 0
                ? t(
                    "Ce seuil couvre toutes vos charges jusqu'à la paie du {{date}}.",
                    { date: textDate(plan[plan.length - 1].nextPayday) },
                  )
                : t(
                    "Toutes vos charges sont couvertes jusqu'au {{date}}. Pour les charges seules, {{hours}} suffiraient.",
                    {
                      date: textDate(plan[plan.length - 1].nextPayday),
                      hours: hoursLabel(current.threshold),
                    },
                  )}
            </Text>
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              {t('{{days}} · payée le {{payday}}', {
                days: workDays(
                  isSunday ? addDays(current.start, 1) : current.start,
                  current.end,
                ),
                payday: longDate(current.payday),
              })}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Figure
                label={t('À {{hours}}, il reste', {
                  hours: hoursLabel(fortnightPlanned),
                })}
                value={
                  fortnight.margin >= 0
                    ? `+ ${money(fortnight.margin)}`
                    : money(fortnight.margin)
                }
                color={
                  fortnight.margin >= 0
                    ? theme.noticeTextLight
                    : theme.errorText
                }
              />
              <Figure
                label={t('Chaque heure en plus')}
                value={`+ ${money(netPerExtraHour(pay, Math.max(fortnightPlanned, pay.regularHoursPerWeek)))}`}
              />
            </View>
          </Section>
        )}

        {following.length > 0 && (
          <Section title={t('Les semaines suivantes')}>
            {following.map(week => (
              <WeekRow key={week.start} week={week} today={today} />
            ))}
            <Button
              variant="bare"
              onPress={() => navigate('/quinzaine/parcours')}
              style={{
                minHeight: 44,
                borderTop: `1px solid ${theme.tableBorder}`,
                borderRadius: 0,
                fontSize: 13,
                fontWeight: 600,
                color: theme.pageTextLink,
              }}
            >
              {t("Toutes les semaines jusqu'en {{month}}", {
                month: new Intl.DateTimeFormat('fr-CA', {
                  month: 'long',
                  timeZone: 'UTC',
                }).format(
                  new Date(`${plan[plan.length - 1].payday}T00:00:00Z`),
                ),
              })}
            </Button>
          </Section>
        )}

        {worked && (
          <Section
            title={
              <View>
                <Text style={{ fontSize: 15, fontWeight: 600 }}>
                  {t('Les deux semaines déjà faites')}
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.pageTextSubdued,
                  }}
                >
                  {t('payées {{when}} · environ {{net}}', {
                    when: whenPaid(worked.payday, today),
                    net: money(worked.net),
                  })}
                </Text>
              </View>
            }
          >
            {workedWeeks.map((week, index) => (
              <Line
                key={week.start}
                last={index === workedWeeks.length - 1}
                title={weekLabel(week.offset, today)}
                detail={weekRange(week.start, week.end)}
                value={
                  <Input
                    aria-label={t('Heures faites {{range}}', {
                      range: weekRange(week.start, week.end),
                    })}
                    inputMode="decimal"
                    defaultValue={String(hoursOf(worked)[index === 0 ? 0 : 1])}
                    onUpdate={value =>
                      setHours(worked, index === 0 ? 0 : 1, value)
                    }
                    style={{ width: 88, minHeight: 44, textAlign: 'right' }}
                  />
                }
              />
            ))}
          </Section>
        )}

        <BanksBanner plan={plan} today={today} />

        {open && (
          <Section title={t('Mon objectif de la semaine')} style={{ gap: 0 }}>
            <View style={{ padding: 16, gap: 10 }}>
              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
              >
                <Input
                  aria-label={t("Objectif d'heures pour cette semaine")}
                  inputMode="decimal"
                  placeholder={String(planned[week])}
                  value={target}
                  onChangeValue={setTarget}
                  style={{ width: 96, minHeight: 44, textAlign: 'right' }}
                />
                <Text style={{ color: theme.pageTextSubdued }}>
                  {t('heures cette semaine')}
                </Text>
              </View>
              {targetMargin !== null && targetHours !== null ? (
                <>
                  <Text
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color:
                        targetMargin >= 0
                          ? theme.noticeTextLight
                          : theme.errorText,
                      ...styles.tnum,
                    }}
                  >
                    {targetMargin >= 0
                      ? t("{{amount}} pour l'investissement", {
                          amount: money(targetMargin),
                        })
                      : t('Il manquerait {{amount}}', {
                          amount: money(-targetMargin),
                        })}
                  </Text>
                  <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                    {t(
                      "À {{hours}} cette semaine, contre {{current}} aujourd'hui prévus : {{diff}} de différence.",
                      {
                        hours: hoursLabel(targetHours),
                        current: hoursLabel(planned[week]),
                        diff: money(targetMargin - open.margin),
                      },
                    )}
                  </Text>
                  <Button onPress={keepTarget} style={{ minHeight: 44 }}>
                    <Trans>Retenir cet objectif</Trans>
                  </Button>
                </>
              ) : (
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  {t(
                    "Saisissez un nombre d'heures pour voir ce qu'il dégage une fois toutes les charges payées.",
                  )}
                </Text>
              )}
            </View>
          </Section>
        )}

        <Section
          title={t('Échéances des {{days}} jours', { days: UPCOMING_DAYS })}
          aside={money(upcomingTotal)}
        >
          {upcoming.length === 0 && (
            <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
              {t('Aucune échéance dans cette période.')}
            </Text>
          )}
          {upcoming.map((charge, index) => {
            const label = coverageLabel(charge.coverage);
            return (
              <Line
                key={charge.id}
                last={index === upcoming.length - 1}
                lead={shortDate(charge.date)}
                title={charge.name}
                detail={label.text}
                detailColor={label.color}
                value={money(-charge.amount)}
              />
            );
          })}
        </Section>

        <Button
          onPress={() => navigate('/quinzaine/a-payer')}
          style={{ minHeight: 48, fontWeight: 600 }}
        >
          <Trans>Ce que je dois payer</Trans>
        </Button>

        <Button
          variant="primary"
          onPress={() => navigate('/quinzaine/paie')}
          style={{ minHeight: 48, fontWeight: 600 }}
        >
          {t("C'est jour de paie")}
        </Button>
      </View>
    </MtxPage>
  );
}

function Figure({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <View
      style={{
        flex: 1,
        padding: 10,
        gap: 2,
        borderRadius: 4,
        border: `1px solid ${theme.tableBorder}`,
        backgroundColor: theme.tableHeaderBackground,
      }}
    >
      <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
        {label}
      </Text>
      <Text style={{ fontSize: 18, fontWeight: 600, color, ...styles.tnum }}>
        {value}
      </Text>
    </View>
  );
}
